import { useAuth } from "../contexts/AuthContext"

export const usePermissions = () => {
    const { permissions, hasPermission, hasAnyPermission, hasAllPermissions, user } = useAuth();

    const can = (permission: string): boolean => {
        return hasPermission(permission)
    }

    const canAny = (permList: string[]): boolean => {
        return hasAnyPermission(permList)
    }

    // Check if user has all of the given permissions
    const canAll = (permList: string[]): boolean => {
        return hasAllPermissions(permList);
    };

    // Check if user is admin (super admin)
    const isAdmin = (): boolean => {
        return user?.role?.name?.toLowerCase() === 'admin';
    };

    // Convenience methods
    const canViewDashboard = () => can('dashboard:view');

    const canManageUsers = () => canAny(['user:create', 'user:update', 'user:delete', 'user:read', 'user:list']);
    const canViewUsers = () => can('user:list');
    const canCreateUser = () => can('user:create');
    const canEditUser = () => can('user:update');
    const canDeleteUser = () => can('user:delete');

    const canManageRoles = () => canAny(['role:create', 'role:update', 'role:delete', 'role:list', 'role:read']);
    const canViewRoles = () => can('role:list');
    const canCreateRole = () => can('role:create');
    const canEditRole = () => can('role:update');
    const canDeleteRole = () => can('role:delete');

    const canManageEmployees = () => canAny(['employee:create', 'employee:read', 'employee:update', 'employee:delete']);
    const canViewEmployees = () => can('employee:list');
    const canCreateEmployee = () => can('employee:create');
    const canEditEmployee = () => can('employee:update');
    const canDeleteEmployee = () => can('employee:delete');

    const canManageInventory = () => canAny(['inventory:create', 'inventory:update', 'inventory:read', 'inventory:delete','inventory:list']);
    const canViewInventory = () => can('inventory:list');
    const canCreateInventory = () => can('inventory:create');
    const canDeleteInventory = () => can('inventory:delete');
    const canUpdateInventory = () => can('inventory:delete');

    const canManageEnquiries = () => canAny(['enquiry:create', 'enquiry:read', 'enquiry:update', 'enquiry:delete', 'enquiry:assign', 'enquiry:change-status']);
    const canViewEnquiries = () => can('enquiry:list');
    const canCreateEnquiry = () => can('enquiry:create');
    const canAssignEnquiry = () => can('enquiry:assign');

    const canManageReminders = () => canAny(['reminder:create', 'reminder:read', 'reminder:update', 'reminder:delete', 'reminder:change-status']);
    const canViewReminders = () => can('reminder:list');
    const canCreateReminder = () => can('reminder:create');

    const canManageReturns = () => canAny(['product-return:create', 'product-return:read', 'product-return:update', 'product-return:delete', 'product-return:change-status']);
    const canViewReturns = () => can('product-return:list');

    const canManageParties = () => canAny(['party:create', 'party:read', 'party:update', 'party:delete']);
    const canViewParties = () => can('party:list');
    const canCreateParties = () => can('party:create');
    const canDeleteParties = () => can('party:delete');
    const canUpdateParties = () => can('party:update');

    const canManageVendors = () => canAny(['vendor:create', 'vendor:read', 'vendor:update', 'vendor:delete']);
    const canViewVendors = () => can('vendor:list');
    const canCreateVendors = () => can('vendor:create');
    const canDeleteVendors = () => can('vendor:delete');
    const canUpdateVendors = () => can('vendor:update');

    const canViewReports = () => can('report:view');
    const canExportReports = () => can('report:export');
    const canPrintReports = () => can('report:print');

    const canUpdateSettings = () => can('settings:update');
    const canViewSettings = () => can('settings:view');

    const canManageSalesInvoice = () => canAny(['sales-invoice:create', 'sales-invoice:read', 'sales-invoice:update', 'sales-invoice:delete', 'sales-invoice:history']);
    const canViewSalesInvoice = () => can('sales-invoice:list');
    const canPrintSalesInvoice = () => can('sales-invoice:print');
    const canDeleteSalesInvoice = () => can('sales-invoice:delete');

    const canManageQuotation = () => canAny(['quotation:create', 'quotation:read', 'quotation:update', 'quotation:delete']);
    const canViewQuotation = () => can('quotation:list');
    const canConvertQuotation = () => can('quotation:convert');
    const canPrintQuotation = () => can('quotation:print');
    const canDeleteQuotation = () => can('quotation:delete');

    const canManagePaymentIn = () => canAny(['payment-in:create', 'payment-in:read', 'payment-in:update', 'payment-in:delete', 'payment-in:list', 'payment-in:print']);
    const canListPaymentIn = () => can('payment-in:list')
    const canPrintPaymentIn = () => can('payment-in:print')
    const canDeletePaymentIn = () => can('payment-in:delete')

    const canManageSalesReturn = () => canAny(['sales-return:create', 'sales-return:read', 'sales-return:update', 'sales-return:delete', 'sales-return:list', 'sales-return:print']);
    const canListSalesReturn = () => can('sales-return:list')
    const canPrintSalesReturn = () => can('sales-return:print')
    const canDeleteSalesReturn = () => can('sales-return:delete')

    const canManageCreditNote = () => canAny(['credit-note:create', 'credit-note:read', 'credit-note:update', 'credit-note:delete', 'credit-note:list', 'credit-note:print']);
    const canListCreditNote = () => can('credit-note:list')
    const canPrintCreditNote = () => can('credit-note:print')
    const canDeleteCreditNote = () => can('credit-note:delete')

    const canManagePurchaseInvoice = () => canAny(['purchase-invoice:create', 'purchase-invoice:read', 'purchase-invoice:update', 'purchase-invoice:delete', 'purchase-invoice:history']);
    const canViewPurchaseInvoice = () => can('purchase-invoice:list');
    const canPrintPurchaseInvoice = () => can('purchase-invoice:print');
    const canDeletePurchaseInvoice = () => can('purchase-invoice:delete');

    const canManagePaymentOut = () => canAny(['payment-out:create', 'payment-out:read', 'payment-out:update', 'payment-out:delete', 'payment-out:list', 'payment-out:print']);
    const canListPaymentOut = () => can('payment-out:list')
    const canPrintPaymentOut = () => can('payment-out:print')
    const canDeletePaymentOut = () => can('payment-out:delete')

    const canManagePurchaseReturn = () => canAny(['purchase-return:create', 'purchase-return:read', 'purchase-return:update', 'purchase-return:delete', 'purchase-return:list', 'purchase-return:print']);
    const canListPurchaseReturn = () => can('purchase-return:list')
    const canPrintPurchaseReturn = () => can('purchase-return:print')
    const canDeletePurchaseReturn = () => can('purchase-return:delete')

    const canManageDebitNote = () => canAny(['debit-note:create', 'debit-note:read', 'debit-note:update', 'debit-note:delete', 'debit-note:list', 'debit-note:print']);
    const canListDebitNote = () => can('debit-note:list')
    const canPrintDebitNote = () => can('debit-note:print')
    const canDeleteDebitNote = () => can('debit-note:delete')

    const canManageLedger = () => canAny(['ledger:create', 'ledger:read', 'ledger:update']);
    const canPrintLedger = () => can('ledger:print');

    const canManagePriceList = () => canAny(['price-list:create', 'price-list:read', 'price-list:move', 'price-list:update', 'price-list:delete', 'price-list:upload']);
    const canViewPriceList = () => can('price-list:list');
    const canDeletePriceList = () => can('price-list:delete');

    const canManageAttendance = () => canAny(['attendance:create','attendance:update']);
    const canViewAttendance = () => can('attendance:read');
    const canCheckAttendance = () => can('attendance:check');
    const canUpdateAttendance = () => can('attendance:update');

    const canUpdateProfile = () => can('profile:update');
    const canViewProfile = () => can('profile:read');

    const canManageCars = () => canAny(['car:create', 'car:read', 'car:update', 'car:delete']);
    const canListCars = () => can('car:list')
    const canDeleteCars = () => can('car:delete')

    const canManageCategories = () => canAny(['category:create', 'category:read', 'category:update', 'category:delete']);
    const canListCategories = () => can('category:list')
    const canDeleteCategories = () => can('category:delete')

    const canManageBrands = () => canAny(['brand:create', 'brand:read', 'brand:update', 'brand:delete','brand:list']);
    const canListBrands = () => can('brand:list')
    const canDeleteBrands = () => can('brand:delete')

    const canViewPriceCode = () => can('price-code:read');
    const canUpdatePriceCode = () => can('price-code:update');

    const canListAccount = () => can('account:list');
    const canListSales = () => can('sales:list');
    const canListPurchase = () => can('purchase:list');
    const canListConfiguration = () => can('configuration:list');
    const canListControlPanel = () => can('control-panel:list');

    return {
        permissions,
        can,
        canAny,
        canAll,
        isAdmin,
        canListAccount,
        canListSales,
        canListPurchase,
        canListConfiguration,
        canListControlPanel,
        canViewDashboard,
        canManageUsers,
        canViewUsers,
        canCreateUser,
        canEditUser,
        canDeleteUser,
        canManageRoles,
        canViewRoles,
        canCreateRole,
        canEditRole,
        canDeleteRole,
        canManageEmployees,
        canViewEmployees,
        canCreateEmployee,
        canEditEmployee,
        canDeleteEmployee,
        canManageInventory,
        canManageSalesReturn,
        canListSalesReturn,
        canPrintSalesReturn,
        canViewInventory,
        canManageEnquiries,
        canViewEnquiries,
        canCreateEnquiry,
        canAssignEnquiry,
        canManageReturns,
        canViewReturns,
        canManageParties,
        canViewParties,
        canManageVendors,
        canViewVendors,
        canViewReports,
        canExportReports,
        canPrintReports,
        canUpdateSettings,
        canViewSettings,
        canManageSalesInvoice,
        canViewSalesInvoice,
        canPrintSalesInvoice,
        canManageCreditNote,
        canPrintCreditNote,
        canListCreditNote,
        canManagePaymentIn,
        canPrintPaymentIn,
        canListPaymentIn,
        canManagePurchaseInvoice,
        canViewPurchaseInvoice,
        canPrintPurchaseInvoice,
        canManagePaymentOut,
        canListPaymentOut,
        canPrintPaymentOut,
        canManagePurchaseReturn,
        canListPurchaseReturn,
        canPrintPurchaseReturn,
        canManageQuotation,
        canPrintQuotation,
        canViewQuotation,
        canConvertQuotation,
        canManageDebitNote,
        canListDebitNote,
        canPrintDebitNote,
        canManageLedger,
        canPrintLedger,
        canManagePriceList,
        canViewPriceList,
        canManageAttendance,
        canViewAttendance,
        canCheckAttendance,
        canUpdateProfile,
        canViewProfile,
        canUpdateAttendance,
        canManageCars,
        canListCars,
        canDeleteCars,
        canManageCategories,
        canListCategories,
        canDeleteCategories,
        canManageBrands,
        canListBrands,
        canDeleteBrands,
        canViewPriceCode,
        canUpdatePriceCode,

        canCreateInventory,
        canDeleteInventory,
        canUpdateInventory,

        canCreateParties,
        canDeleteParties,
        canUpdateParties,

        canCreateVendors,
        canDeleteVendors,
        canUpdateVendors,

        canDeletePriceList,

        canDeleteSalesInvoice,

        canDeleteQuotation,

        canDeletePaymentIn,

        canDeleteSalesReturn,

        canDeleteCreditNote,

        canDeletePurchaseInvoice,

        canDeletePaymentOut,

        canDeletePurchaseReturn,

        canDeleteDebitNote,

        canManageReminders,
        canViewReminders,
        canCreateReminder
    };
}

